"use client";

import React, {useMemo} from "react";
import {Context} from "@hotwired/stimulus";
import {useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import * as v from "valibot";

import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController";
import {RhfInput} from "@root/server/client/esweb/components/ui/rhf/RhfInput";
import {RhfTextarea} from "@root/server/client/esweb/components/ui/rhf/RhfTextarea";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {t} from "@root/server/client/esweb/src/lib/i18n";
import {OrderOfferFormSchema} from "@root/server/client/esweb/src/pages/app/tenants/tenant_shemas.ts";



export const name = "app--tenants--order_offer_form";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <OrderOfferFormStimulus {...props} />);
    }
}

type OrderOfferFormValues = v.InferInput<typeof OrderOfferFormSchema>;

interface OrderOfferFormProps {
    submitUrl: string;
    initialValues?: Partial<OrderOfferFormValues>;
}

function OrderOfferFormStimulus({submitUrl, initialValues}: OrderOfferFormProps) {
    const defaultValues = useMemo<OrderOfferFormValues>(() => ({
        message: initialValues?.message ?? "",
        expected_price: initialValues?.expected_price,
    }), [initialValues]);

    const {control, handleSubmit} = useForm<OrderOfferFormValues>({
        resolver: valibotResolver(OrderOfferFormSchema),
        defaultValues,
        mode: "onTouched",
        reValidateMode: "onChange",
    });

    const {execute, isSubmitting, serverErrors} = useFormSubmission<OrderOfferFormValues>({submitUrl});

    return (
        <form className="space-y-6" onSubmit={handleSubmit(execute)}>
            <div className="space-y-4">
                <RhfTextarea
                    control={control}
                    name="message"
                    label={t({cs: "Zpráva pro zákazníka *", en: "Message to customer *"})}
                    textareaProps={{
                        placeholder: t({
                            cs: "Popište vaši nabídku, dostupnost a další detaily",
                            en: "Describe your offer, availability, and other details",
                        }),
                        rows: "6",
                        maxLength: "2000",
                    }}
                />

                <RhfInput
                    control={control}
                    name="expected_price"
                    label={t({cs: "Navrhovaná cena (Kč) *", en: "Proposed price (CZK) *"})}
                    type="number"
                    inputProps={{
                        placeholder: t({cs: "např. 1500 nebo 1999,99", en: "e.g. 1500 or 1999.99"}),
                    }}
                />
            </div>

            <FormServerErrors errors={serverErrors} />

            <div className="flex justify-end">
                <ContinueSubmitButton {...{isSubmitting}}>
                    {t({cs: "Odeslat nabídku", en: "Submit offer"})}
                </ContinueSubmitButton>
            </div>
        </form>
    );
}
